import { Position } from "@xyflow/react";
import { parkFocusSheet, portSide } from "./ports.js";

const ARCH_BASE_GAP = 80;
const CIRCUIT_GAP = 48;
const CIRCUIT_ROW = 720;

export function cardSize(m, { sheet } = {}) {
  const ports = m?.ports || [];
  const left = ports.filter((p) => portSide(p) === Position.Left).length;
  const right = ports.filter((p) => portSide(p) === Position.Right).length;
  const vert = ports.filter((p) => {
    const s = portSide(p);
    return s === Position.Top || s === Position.Bottom;
  }).length;
  const longest = ports.reduce((n, p) => Math.max(n, String(p.name || "").length), 4);
  const h = Math.max(76 + 14 * Math.max(left, right, 1), 56 + 14 * Math.max(vert, 1));
  const passive = m?.kind === "passive" || String(m?.name || "").includes("/");
  if (passive) {
    return { w: Math.max(148, 56 + 5 * Math.min(String(m.name || "").length, 22)), h: Math.max(72, 56 + 14 * Math.max(vert, 1)) };
  }
  const w = sheet ? Math.max(220, 72 + 8 * longest) : Math.max(168, 64 + 7 * longest);
  return { w, h };
}

/** Extra gap after a card: more ports → taller tower → more air to the next block. */
export function architectureGap(ports, h) {
  const n = Array.isArray(ports) ? ports.length : Number(ports) || 0;
  return Math.round(ARCH_BASE_GAP + Math.min(n, 28) * 4 + Math.max(0, (h || 0) - 140) * 0.12);
}

/**
 * One architecture row. Compiler x only sets order. Width/height from ports, not container w/h.
 * @returns {Map<string,{x,y,w,h}>}
 */
export function layoutArchitecture(mods) {
  const items = (mods || []).filter(Boolean).map((m) => {
    const sheet = m.kind === "sheet" || m.role === "sheet_instance";
    const box = cardSize(m, { sheet });
    return { m, ...box, ox: m.x ?? 0 };
  });
  items.sort((a, b) => a.ox - b.ox || String(a.m.name).localeCompare(String(b.m.name)));
  const out = new Map();
  let x = 0;
  for (const it of items) {
    out.set(it.m.name, { x, y: 0, w: it.w, h: it.h });
    x += it.w + architectureGap(it.m.ports, it.h);
  }
  return out;
}

function childRank(m) {
  if (!m) return 2;
  if (m.kind === "passive" || String(m.name || "").includes("/")) return 2;
  const kind = m.subcircuitKind || m.subcircuit_kind || "";
  if (kind === "chip_core" || (m.ports || []).length >= 12) return 0;
  return 1;
}

/**
 * Circuit page: parked rail at origin, children packed to its right.
 * Ignores compiler container coordinates (the 1904×176 DUT box).
 */
export function layoutCircuit(focusMod, children) {
  const parkSz = parkFocusSheet(focusMod || { ports: [] }, []);
  const park = { x: 0, y: 0, w: parkSz.w, h: parkSz.h };
  const out = new Map();
  if (focusMod?.name) out.set(focusMod.name, park);

  const kids = [...(children || [])].sort(
    (a, b) => childRank(a) - childRank(b) || (a.x ?? 0) - (b.x ?? 0) || String(a.name).localeCompare(String(b.name)),
  );
  const chips = kids.filter((m) => childRank(m) === 0);
  const banks = kids.filter((m) => childRank(m) === 1);
  const passives = kids.filter((m) => childRank(m) === 2);

  const originX = park.w + CIRCUIT_GAP;
  let x = originX;
  let y = 0;
  let rowH = 0;

  const place = (m, sheet, wrapFrom) => {
    const s = cardSize(m, { sheet });
    if (wrapFrom != null && x > wrapFrom && x + s.w > wrapFrom + CIRCUIT_ROW) {
      x = wrapFrom;
      y += rowH + CIRCUIT_GAP;
      rowH = 0;
    }
    out.set(m.name, { x, y, w: s.w, h: s.h });
    x += s.w + CIRCUIT_GAP;
    rowH = Math.max(rowH, s.h);
    return s;
  };

  for (const m of chips) place(m, false, null);
  if (banks.length) {
    if (!chips.length) {
      x = originX;
      y = 0;
      rowH = 0;
    }
    for (const m of banks) {
      const sheet = m.kind === "sheet" || m.role === "sheet_instance";
      place(m, sheet, originX);
    }
  }
  if (passives.length) {
    x = originX;
    y += rowH + CIRCUIT_GAP;
    rowH = 0;
    for (const m of passives) place(m, false, originX);
  }

  if (focusMod?.name) {
    const chipH = chips.reduce((n, m) => Math.max(n, out.get(m.name)?.h || 0), 0);
    park.h = Math.max(park.h, chipH);
    out.set(focusMod.name, park);
  }
  return out;
}

export function boxesOverlap(a, b, pad = 0) {
  if (!a || !b) return false;
  return !(
    a.x + a.w + pad <= b.x ||
    b.x + b.w + pad <= a.x ||
    a.y + a.h + pad <= b.y ||
    b.y + b.h + pad <= a.y
  );
}
